// AIDEV-NOTE: EVO-1813 — distinguishes a real buffering stall from intentional
// scripted silence. Meditation pauses are real encoded audio (zero-PCM → AAC),
// so during silence the audio element keeps playing: currentTime advances and
// no `waiting` event fires. A genuine stall (MSE buffer gap, HLS segment not
// yet available) fires `waiting`/`stalled` and currentTime freezes at the
// buffered edge. This detector debounces those signals so the UI only shows
// "Preparing audio…" for stalls that persist past STALL_INDICATOR_DEBOUNCE_MS.

// AIDEV-NOTE: ~2s debounce avoids indicator flicker on tiny rebuffers.
export const STALL_INDICATOR_DEBOUNCE_MS = 2_000;

// AIDEV-NOTE: timeupdate granularity is ~250ms of media time; require a small
// minimum advance so float noise at the buffered edge doesn't count as progress.
export const STALL_PROGRESS_EPSILON_SECONDS = 0.05;

export interface StallDetectorCallbacks {
    onStallDetected: () => void;
    onStallCleared: () => void;
}

// AIDEV-NOTE: Alerting design — cumulative stall stats reported in the
// playback-end beacon. They let the server distinguish a real unrecovered stall
// (interrupt → alert) from a user simply abandoning playback (benign), which is
// the one client-side case that otherwise forces a recall/false-positive
// tradeoff.
export interface StallStats {
    /** Number of confirmed (post-debounce) stalls during this playback. */
    stallCount: number;
    /** Longest single confirmed stall in ms (includes an in-progress stall). */
    maxStallMs: number;
    /** Whether a confirmed stall is active right now (e.g. at unload). */
    stalledNow: boolean;
}

/**
 * Debounced stall tracker for a media element.
 *
 * Wiring contract (see AudioController.setupMainEventListeners):
 * - `waiting` / `stalled` events → notifyWaiting()
 * - `playing` event → notifyPlaying()
 * - `timeupdate` event → notifyTimeUpdate(currentTime)
 * - teardown (stop/new session) → reset()
 */
export class StallDetector {
    private debounceTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private stalled = false;
    private lastObservedTimeSeconds: number | null = null;
    private stallCount = 0;
    private maxStallMs = 0;
    private stallStartedAtMs: number | null = null;

    constructor(
        private callbacks: StallDetectorCallbacks,
        private debounceMs: number = STALL_INDICATOR_DEBOUNCE_MS,
    ) {}

    get isStalled(): boolean {
        return this.stalled;
    }

    /** Cumulative stall stats for the playback-end beacon. */
    getStats(): StallStats {
        const ongoingMs =
            this.stalled && this.stallStartedAtMs !== null
                ? Date.now() - this.stallStartedAtMs
                : 0;
        return {
            stallCount: this.stallCount,
            maxStallMs: Math.max(this.maxStallMs, ongoingMs),
            stalledNow: this.stalled,
        };
    }

    /** A `waiting`/`stalled` signal — arm the debounce if not already armed. */
    notifyWaiting(): void {
        if (this.stalled || this.debounceTimeoutId !== null) {
            return;
        }
        this.debounceTimeoutId = setTimeout(() => {
            this.debounceTimeoutId = null;
            this.stalled = true;
            this.stallCount += 1;
            this.stallStartedAtMs = Date.now();
            this.callbacks.onStallDetected();
        }, this.debounceMs);
    }

    /** A `playing` signal — playback resumed, drop pending/active stall. */
    notifyPlaying(): void {
        this.clear();
    }

    /**
     * A `timeupdate` signal. Only counts as recovery when currentTime actually
     * moved — during a stall the clock freezes at the buffered edge.
     */
    notifyTimeUpdate(currentTimeSeconds: number): void {
        const last = this.lastObservedTimeSeconds;
        this.lastObservedTimeSeconds = currentTimeSeconds;
        if (last === null) {
            return;
        }
        if (
            Math.abs(currentTimeSeconds - last) > STALL_PROGRESS_EPSILON_SECONDS
        ) {
            this.clear();
        }
    }

    /**
     * Drop pending/active stall and zero the cumulative stats (teardown path
     * for a fresh playback session). Read getStats() BEFORE calling this.
     */
    reset(): void {
        if (this.debounceTimeoutId !== null) {
            clearTimeout(this.debounceTimeoutId);
            this.debounceTimeoutId = null;
        }
        this.stalled = false;
        this.lastObservedTimeSeconds = null;
        this.stallCount = 0;
        this.maxStallMs = 0;
        this.stallStartedAtMs = null;
    }

    private clear(): void {
        if (this.debounceTimeoutId !== null) {
            clearTimeout(this.debounceTimeoutId);
            this.debounceTimeoutId = null;
        }
        if (this.stalled) {
            if (this.stallStartedAtMs !== null) {
                this.maxStallMs = Math.max(
                    this.maxStallMs,
                    Date.now() - this.stallStartedAtMs,
                );
                this.stallStartedAtMs = null;
            }
            this.stalled = false;
            this.callbacks.onStallCleared();
        }
    }
}
